import React, { useEffect, useState } from "react";
import { Container, PageHero, Overline } from "@/components/ui-blocks";
import { ASSETS, COMPANY, MOCK_MULTIMEDIA } from "@/data/mockData";
import { fetchMultimedia } from "@/services/api";
import Seo from "@/components/Seo";
import { Play, ArrowUpRight, Instagram, Facebook, Youtube, Music2, Newspaper } from "lucide-react";

export default function Multimedia() {
  const [all, setAll] = useState(MOCK_MULTIMEDIA);

  useEffect(() => {
    // Live catalogue from Firestore; fall back to mock if empty/unavailable.
    fetchMultimedia()
      .then((m) => { if (m && m.length) setAll(m); })
      .catch(() => {});
  }, []);

  const videos = all.filter((m) => m.type !== "article");
  const articles = all.filter((m) => m.type === "article");

  const featured = videos.find((m) => m.featured) || videos[0];
  const rest = featured ? videos.filter((m) => m.id !== featured.id) : videos;

  return (
    <div data-testid="multimedia-page">
      <Seo title="Multimedia & Podcast" description="Chantal & Co — a premium podcast and video series on South-east Asian heritage, plus investigative press features." />
      <PageHero
        testId="multimedia-hero"
        overline="Ace Asia Multimedia"
        title="Chantal & Co — heritage, in conversation."
        subtitle="A premium, interview-driven podcast series that dives into the soul of South-east Asia's built and living heritage."
      />

      {/* Editorial intro */}
      <section className="bg-[#FDFBF7] py-16">
        <Container>
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-7 text-[#4A3728] leading-relaxed text-lg space-y-5">
              <p>
                Each episode brings together leading historians, architects,
                and conservationists to unpack the delicate balance between
                rapid urban modernisation and cultural conservation.
              </p>
              <p>
                From the architectural brilliance of historical landmarks in
                Singapore to fading community traditions, the show explores
                how our past shapes our future. Through curated slots,
                forward-thinking brands and institutions have a dedicated
                platform to share their own heritage stories and preservation
                milestones with an engaged, culturally conscious audience.
              </p>
            </div>
            <div className="lg:col-span-5">
              <img
                src={ASSETS.founderEditorial}
                alt="Chantal Sajan in studio"
                data-testid="multimedia-side-image"
                className="w-full aspect-[4/5] object-cover"
              />
              <div className="text-xs text-[#946f36] mt-2">A snapshot from the video series.</div>
            </div>
          </div>
        </Container>
      </section>

      {/* Featured Episode */}
      {featured && (
        <section className="bg-[#281a12] text-[#FDFBF7] grain relative" data-testid="multimedia-featured">
          <Container className="relative z-10 py-20">
            <Overline className="text-[#946f36]"><span className="bg-[#946f36]" /> Featured Episode</Overline>
            <div className="mt-6 grid lg:grid-cols-12 gap-12 items-center">
              <a
                href={featured.youtubeUrl}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="multimedia-featured-link"
                className="lg:col-span-7 block group relative overflow-hidden border border-[#946f36]/30 aspect-video"
              >
                <img src={featured.thumbnail} alt={featured.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-[#281a12]/40 flex items-center justify-center">
                  <span className="w-20 h-20 rounded-full border-2 border-[#FDFBF7] flex items-center justify-center bg-[#281a12]/60 backdrop-blur-sm">
                    <Play className="w-8 h-8 text-[#FDFBF7] ml-1" strokeWidth={1.5} />
                  </span>
                </div>
              </a>
              <div className="lg:col-span-5">
                <div className="overline text-[#946f36]">{featured.season} · {featured.duration}</div>
                <h3 className="mt-3 font-serif-display text-4xl leading-tight">{featured.title}</h3>
                <p className="mt-4 text-[#D1C3B4] leading-relaxed">{featured.description}</p>
                <a
                  href={COMPANY.socials.youtube}
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid="multimedia-watch-youtube"
                  className="mt-7 inline-flex items-center gap-2 bg-[#946f36] hover:bg-[#FDFBF7] hover:text-[#281a12] text-[#281a12] px-7 py-3.5 text-xs uppercase tracking-[0.2em] transition-colors"
                >
                  Watch on YouTube <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
                </a>
              </div>
            </div>
          </Container>
        </section>
      )}

      {/* Episode grid */}
      {rest.length > 0 && (
        <section className="bg-[#FDFBF7] py-20">
          <Container>
            <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
              <h2 className="font-serif-display text-4xl md:text-5xl font-light">More Episodes</h2>
              <a href={COMPANY.socials.youtube} target="_blank" rel="noreferrer noopener" data-testid="multimedia-channel-link" className="overline text-[#6e5022] hover:text-[#946f36]">
                View Channel →
              </a>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="multimedia-episode-grid">
              {rest.map((ep, i) => (
                <a
                  key={ep.id + "-" + i}
                  href={ep.youtubeUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid={`multimedia-episode-${ep.id}`}
                  className="group block aa-fade-up"
                  style={{ animationDelay: `${i * 0.08}s`, opacity: 0 }}
                >
                  <div className="relative aspect-video bg-[#F4F1EA] overflow-hidden">
                    <img src={ep.thumbnail} alt={ep.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-[#281a12]/20 group-hover:bg-[#281a12]/10 transition-colors flex items-end p-4">
                      {ep.duration && <span className="text-[10px] uppercase tracking-[0.18em] bg-[#281a12]/80 text-[#FDFBF7] px-2 py-1">{ep.duration}</span>}
                    </div>
                  </div>
                  <div className="pt-5">
                    <div className="overline text-[#946f36]">{ep.season}</div>
                    <div className="font-serif-display text-2xl mt-2 leading-snug group-hover:text-[#6e5022]">{ep.title}</div>
                    <p className="text-sm text-[#4A3728] mt-2 line-clamp-2">{ep.description}</p>
                  </div>
                </a>
              ))}
            </div>
          </Container>
        </section>
      )}

      {/* In the Press — online journalism features */}
      {articles.length > 0 && (
        <section className="bg-[#F4F1EA] py-20" data-testid="multimedia-articles">
          <Container>
            <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
              <div>
                <Overline><span className="bg-[#946f36]" /> In the Press</Overline>
                <h2 className="mt-4 font-serif-display text-4xl md:text-5xl font-light">Investigative writing & features.</h2>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="multimedia-article-grid">
              {articles.map((a, i) => (
                <a
                  key={a.id + "-" + i}
                  href={a.articleUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid={`multimedia-article-${a.id}`}
                  className="group block bg-[#FDFBF7] border border-[#6e5022]/15 hover:border-[#6e5022]/40 transition-colors aa-fade-up"
                  style={{ animationDelay: `${i * 0.08}s`, opacity: 0 }}
                >
                  <div className="relative aspect-video bg-[#F4F1EA] overflow-hidden">
                    <img src={a.thumbnail} alt={a.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-5">
                    <div className="overline text-[#946f36] flex items-center gap-2">
                      <Newspaper className="w-3.5 h-3.5" strokeWidth={1.5} /> {a.source || "Feature"}
                    </div>
                    <div className="font-serif-display text-2xl mt-2 leading-snug group-hover:text-[#6e5022]">{a.title}</div>
                    <p className="text-sm text-[#4A3728] mt-2 line-clamp-3">{a.description}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-[#6e5022] group-hover:text-[#946f36]">
                      Read article <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </span>
                  </div>
                </a>
              ))}
            </div>
          </Container>
        </section>
      )}

      {/* Social */}
      <section className="bg-[#FDFBF7] py-20">
        <Container>
          <div className="max-w-2xl">
            <Overline>Follow the Series</Overline>
            <h2 className="mt-4 font-serif-display text-4xl font-light">Chantal & Co on every channel.</h2>
            <div className="mt-8 flex items-center gap-4 flex-wrap">
              <SocialLink href={COMPANY.socials.youtube} icon={Youtube} label="YouTube" testId="multimedia-social-youtube" />
              <SocialLink href={COMPANY.socials.instagram} icon={Instagram} label="Instagram" testId="multimedia-social-instagram" />
              <SocialLink href={COMPANY.socials.facebook} icon={Facebook} label="Facebook" testId="multimedia-social-facebook" />
              <SocialLink href={COMPANY.socials.tiktok} icon={Music2} label="TikTok" testId="multimedia-social-tiktok" />
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
}

function SocialLink({ href, icon: Icon, label, testId }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      data-testid={testId}
      className="inline-flex items-center gap-3 border border-[#6e5022]/30 px-5 py-3 hover:bg-[#6e5022] hover:text-[#FDFBF7] transition-colors"
    >
      <Icon className="w-4 h-4" strokeWidth={1.5} />
      <span className="text-xs uppercase tracking-[0.18em]">{label}</span>
    </a>
  );
}
